import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';
import { Observable, map } from 'rxjs';
import { AuthService } from './auth.service';
import { PermissionService } from '../core/services/permission.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private permissionService: PermissionService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const isLoggedIn = !!this.authService.getToken();
    if (!isLoggedIn) {
      // '' is the login screen (there is no /login route).
      return this.router.createUrlTree([''], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Platform-operator screens: only the SUPER_ADMIN role, never the
    // tenant Admin (who otherwise passes every role check as a superuser).
    if (route.data['superAdminOnly'] && !this.authService.isSuperAdmin()) {
      return this.router.createUrlTree(['/unauthorized']);
    }

    return this.permissionService.ensureLoaded().pipe(
      map(() => {
        // RAC permission check: route data may pin a pageKey; otherwise the
        // central route map resolves one. Only enforced when permission data
        // actually loaded — on fetch failure we fall back to legacy roles.
        if (this.permissionService.hasPermissionData()) {
          const resolved = route.data['pageKey']
            ? {
                key: route.data['pageKey'] as string,
                full: !!route.data['requiresFull'],
              }
            : this.permissionService.resolveRoute(state.url);
          if (resolved) {
            const allowed = resolved.full
              ? this.permissionService.canEdit(resolved.key)
              : this.permissionService.canView(resolved.key);
            return allowed
              ? true
              : this.router.createUrlTree(['/unauthorized']);
          }
          // Routes not in the page registry keep the legacy role check.
        }

        const allowedRoles = route.data['roles'] as string[] | undefined;
        if (allowedRoles && !this.authService.hasRole(allowedRoles)) {
          return this.router.createUrlTree(['/unauthorized']);
        }
        return true;
      })
    );
  }
}
